<template>
    <div
        class="o-path-otg"
        :style="componentStyles"
    >
        <div class="desktop-container">
            <header class="o-path-otg__header">
                <AIconButton
                    :class="`back-button ${componentParams.back_button_class || ''}`"
                    :icon="componentParams.back_icon ||  'arrow-left-transparent'"
                    :text="$t('ui.back')"
                    @onClick="onClickBack"
                />
                <div 
                    class="o-path-otg__logo"
                    v-if="componentParams.is_show_logo"
                >
                    <div class="o-path-otg__logo-image" />
                </div>
                <OIntegrationCloseButton />
            </header>
            <section class="path-otg-main-section">
                <h3 class="path-otg-title">
                    {{ $t('ui.find-out-at-the-center', { mapName: t(map, 'name')}) }}
                </h3>
                <component
                    :is="startItemsComponent"
                    :startObjectId="startObject.id"
                    @onChangeStart="onStartChange"
                />
                <ODropdownOtg
                    :label="$t('ui.to')"
                    :items="destinationPreparedItems"
                    :value="destinationObject.id"
                    :disabled="isDestinationLocked"
                    class="path-otg-dropdown"
                    @input="onDestinationChange"
                />
                <ACheckbox
                    v-if="isShowCheckbox"
                    v-model="guideModeCheckboxState"
                    :label="$t('ui.show-route-for-wheelchair-and-pram')"
                    class="path-settings-checkbox"
                />
            </section>
            <header 
                v-if="commonConfigParams.is_show_guide_switch"
                class="path-steps-header"
            >
                <span class="path-steps-header-title">
                    {{ switchTitle }}
                </span>
                <OGuideSwitch
                    v-model="guideModeCheckboxState"
                    :trueValue="true"
                    :falseValue="false"
                />
            </header>

            <h3
                v-if="isPathInvalid"
                class="path-otg-steps-error"
            >
                {{ invalidPathMessage }}
            </h3>
            <OStepsPanelOtg
                v-if="activePath"
                class="path-otg-steps-panel"
            />
        </div>
        <div class="mobile-container">
            <OPathPanelOtgMobile
                :destinationObject="destinationObject"
                :gMapsPosition="gMapsPosition || {}"
                :isOutdoorItem="isOutdoorItem"
                :isPathInvalid="isPathInvalid"
                :invalidPathMessage="invalidPathMessage"
            />
        </div>
    </div>
</template>

<script>
import {
    mapState,
    mapActions,
    mapGetters
} from "vuex";

import ACheckbox from '../../atoms/a-checkbox';
import AIconButton from '../../atoms/a-icon-button';
import OGuideSwitch from '../../organisms/o-guide-switch';
import ODropdownOtg from '../../organisms/o-dropdown-otg';
import OStepsPanelOtg from '../../organisms/o-steps-panel-otg';
import ODropdownSelect from '../../organisms/o-dropdown-select';
import OStartItemsDropdown from '../../organisms/o-start-items-dropdown';
import OPathPanelOtgMobile from '../../organisms/o-path-panel-otg-mobile';
import OIntegrationCloseButton from '../../organisms/o-integration-close-button';

import {
    START_VIEW_API,
    UI_MAP_READY,
    UI_STEP_BY_STEP_DIRECTIONS
} from "@/shared/components/map-viewer/constants/map-events";
import {
    FAST,
    EASY
} from "@/utils/constants/app-constants";
import {WEB_INTEGRATION} from "@/utils/constants/route-constants";
import {MAP_SHOW_PATH, REMOVE_PATH} from "@/shared/components/map-viewer/constants/map-commands";
import {getPortalsForCatalogueAZ} from "@/store/getters";

import uniqBy from 'lodash/uniqBy'
import cloneDeep from 'lodash/cloneDeep'

export default {
    name: 'OPathOtg',
    components: {
        ACheckbox,
        AIconButton,
        OGuideSwitch,
        ODropdownOtg,
        OStepsPanelOtg,
        ODropdownSelect,
        OPathPanelOtgMobile,
        OStartItemsDropdown,
        OIntegrationCloseButton
    },
    data(){
        return {
            pathError: false,
            pathErrorTimeout: null,
            guideModeCheckboxState: this.$route.query.mGuideMode == EASY || this.$route.query.mvGuideMode == EASY 
        }
    },
    watch: {
        guideModeCheckboxState() {
            this.$router.push({ query: { ...this.$route.query, mvGuideMode: this.mvGuideMode }})
            this.buildPath();
        }
    },
    computed:{
        ...mapState({
            map: state => state.map,
            activePath: state => state.activePath,
            stepIndex: state => state.activeStepIndex,
        }),
        ...mapGetters([
            't',
            'isAppLoaded',
            'getTenantsAZ',
            'getMyPosition',
            'getMapLoading',
            'getEntrancesAZ',
            'getMapConfigProp',
            'getServicesObjectsAZ',
            'getPortalsForCatalogueAZ',
            'getDenormalisedObjectById',
        ]),
        isDestinationLocked(){
            return Boolean(this.$route.query.mToLocked) || this.getMapConfigProp('components.OPathOtg.params.is_destination_locket_default', true);
        },
        destinationObject() {
            return this.getDenormalisedObjectById(Number(this.$route.query.mTo));
        },
        startObject() {
            return this.getDenormalisedObjectById(Number(this.$route.query.mFrom));
        },
        startObjectName() {
            return this.t(this.startObject, 'name');
        },
        destinationObjectName() {
            return this.t(this.destinationObject, 'name');
        },
        mvGuideMode() {
            return this.guideModeCheckboxState ? EASY : FAST;
        },
        preparedItems() {
            const items = [
                ...this.getPortalsForCatalogueAZ,
                ...this.getTenantsAZ,
                ...this.getServicesObjectsAZ
            ];
            return items.map((el) => {
                return {
                    name: this.t(el, 'name'),
                    ...el
                }
            });
        },
        destinationPreparedItems() {
            if(this.startObject?.id) {
                return this.preparedItems.filter(item => item.id !== this.startObject.id)
            }
            return this.preparedItems
        },
        myPosition() {
            if(this.getMyPosition) {
                const positonData = cloneDeep(this.getDenormalisedObjectById(Number(this.getMyPosition)))
                positonData.name = this.$t('ui.guide.my-position', {name: this.t(positonData, 'name', 'Not set')})
                return positonData
            }
            return {}
        },
        isPathInvalid() {
            return !this.activePath && this.pathError;
        },
        invalidPathMessage() {
            return this.$t('ui.cannot-build-path', { start: this.startObjectName, destination: this.destinationObjectName });
        },
        isStore() {
            return this.$route.name.includes(WEB_INTEGRATION.STORE.ROOT.NAME)
        },
        commonConfigStyles() {
            return this.getMapConfigProp('components.OPathOtg.styles') || {};
        },
        viewConfigStyles() {
            return this.getMapConfigProp(`views.${this.view}.footer.OPathOtg.styles`) || {};
        },
        componentStyles() {
            return {
                ...this.commonConfigStyles,
                ...this.viewConfigStyles
            }
        },
        commonConfigParams() {
            return this.getMapConfigProp('components.OPathOtg.params') || {};
        },
        viewConfigParams() {
            return this.getMapConfigProp(`views.${this.view}.footer.OPathOtg.params`) || {};
        },
        componentParams() {
            return {
                ...this.commonConfigParams,
                ...this.viewConfigParams
            }
        },
        isOutdoorItem() {
            return this.t(this.destinationObject, 'is_google_maps') || false;
        },
        gMapsPosition() {
            return this.t(this.destinationObject, 'gmaps_position');
        },
        isShowTenants() {
            return this.componentParams.is_show_tenants
        },
        startItemsComponent() {
            return this.commonConfigParams.start_items_component || 'OStartItemsDropdown'
        },
        isShowCheckbox() {
            if(this.componentParams.hasOwnProperty('is_show_checkbox')) {
                return this.componentParams.is_show_checkbox
            }
            return true
        },
        switchTitle() {
            return this.guideModeCheckboxState
                ? this.$t('ui.directions-for-wheelchairs')
                : this.$t('ui.show-fastest-way');
        },
    },
    methods: {
        ...mapActions([
            'setActiveStepIndex',
        ]),
        setActivePath(data) {
            this.$store.commit('setActivePathToState', data);
            clearTimeout(this.pathErrorTimeout);
            this.pathError = false;
        },
        onClickBack() {
            this.$bus.$emit(REMOVE_PATH);
            const name = this.isStore ?
                WEB_INTEGRATION.STORE.SETTINGS.NAME :
                WEB_INTEGRATION.CATALOGUE.SETTINGS.NAME;
            this.$router.push({ name, query: { ...this.$route.query, mv: 'OutdoorView' } });
        },
        initEventListeners() {
            this.$bus.$on(UI_MAP_READY, this.checkMapReady);
            this.$bus.$on(UI_STEP_BY_STEP_DIRECTIONS, this.setActivePath);
        },
        deactivateEventListeners() {
            this.$bus.$off(UI_MAP_READY, this.checkMapReady);
            this.$bus.$off(UI_STEP_BY_STEP_DIRECTIONS, this.setActivePath);
        },
        checkMapReady() {
            if (this.isAppLoaded)
                setTimeout( () => this.buildPathAfterLoading(), 300 );

        },
        buildPathAfterLoading() {
            if (this.isAppLoaded && !this.getMapLoading) {
                const delay = this.getMapConfigProp('guide.delay_guide', 0);
                this.guideLoadingTimeout = setTimeout(() => this.buildPath(), delay);
            }
        },
        buildPath() {
            if(
                !this.destinationObject ||
                !this.startObject ||
                !this.isAppLoaded ||
                this.getMapLoading
            ) return;

            this.pathError = false;
            this.$bus.$emit(START_VIEW_API);

            this.setActiveStepIndex(null);
            this.$store.commit('setActivePathToState', null);

            const params = {
                from:{
                    id: this.startObject.id
                },
                to: {
                    id: this.destinationObject.id
                },
                mode: this.mvGuideMode,
            };

            params.portal_rules = this.getMapConfigProp('guide.portal_rules', {});

            const isRedirectFromQR = this.$route.query['redirect-from'] === 'qr';
            if(isRedirectFromQR) {
                const terminalData = this.getDenormalisedObjectById(this.startObject.id);
                const kioskOrientation = terminalData?.meta?.customJSON?.cameras?.kiosk_orientation;

                if(kioskOrientation) {
                    params.initial_orientation = kioskOrientation;
                    params.use_initial_vertex_orientation = false;
                } else {
                    params.use_initial_vertex_orientation = true;
                }
            } else {
                params.use_initial_vertex_orientation = true;
            }

            // params.use_initial_vertex_orientation = true;
            console.log('Request path:', params);
            this.$bus.$emit(MAP_SHOW_PATH, params);
            this.pathErrorTimeout = setTimeout( () => this.pathError = true, 200);
        },
        setActiveStep(step) {
            this.$store.commit('setActiveStep', step);
        },
        setActiveStepIndex(index) {
            this.$store.commit('setActiveStepIndex', index);
        },

        onStartChange(newStartObjectId){
            this.$router.push({ query: { ...this.$route.query, mFrom: newStartObjectId }});
            this.buildPath();
        },
        onDestinationChange(newDestinationObjectId){
            this.$router.push({ query: { ...this.$route.query, mTo: newDestinationObjectId }});
            this.buildPath();
        },
    },
    mounted(){
        this.initEventListeners();
        setTimeout( () => this.buildPathAfterLoading(), 300 );
    },
    beforeDestroy() {
        this.deactivateEventListeners()
    }
}
</script>

<style lang="scss" src="./theme.scss" />
